import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import Avatar from 'components/Avatar';
import FeedPostStatus from 'scenes/FeedPost/components/FeedPostStatus';

import { useApi } from 'hooks/useApi';
import { NetworkActions } from 'helpers/actions/network';
import { CONNECT_STATUS } from 'constants/profile';
import { Role, isAuthCandidate } from 'helpers/user';
import LinkBuilder from 'helpers/link-builder';
import UpgradeRecruiterAccount from 'scenes/Dialog/components/UpgradeRecruiterAccount';

const FeedPostAvatar = props => {
  const { user, post, isMobile, actions } = props;
  const {
    CandidateID,
    CandidatePictureUrl,
    iconType,
    HeroStatus,
    TypeID,
    FirmLogo,
    FirmUrlAlias,
    AllowView,
    ViewProfile,
    ProcessType,
    PublicName,
    ProcessID,
    ViewAsRecruiter,
    FirmTypeName,
    ReviewType,
  } = post;
  const {
    onClickReviewLink,
    onClickApplyHandler,
    onStatusChangeHandler,
    onSendMessageHandler,
  } = actions;

  const api = useApi();

  const [isDisableActions, setDisableActions] = useState(false);

  const [cls, size] = useMemo(
    () =>
      isMobile ? ['feed-post__avatar--mobile', 38] : ['feed-post__avatar', 52],
    []
  );

  const type = Number(TypeID);

  const avatar = useMemo(() => {
    if ([9, 10, 11, 16, 18, 20, 21, 26, 27, 28].indexOf(type) !== -1) {
      return FirmLogo || null;
    }
    return CandidatePictureUrl;
  }, [user]);

  const link = useMemo(() => {
    if (
      isAuthCandidate(user, CandidateID) ||
      (Role.isCandidate(user) &&
        !Role.isRecruiter(user) &&
        ViewProfile === 'No') ||
      (!Role.isCandidate(user) &&
        Role.isRecruiter(user) &&
        ViewAsRecruiter === 'No')
    ) {
      return null;
    }

    if ([1, 2, 3, 4, 5, 6, 7, 8, 19, 22, 24, 25].indexOf(type) !== -1) {
      return (!Role.isCandidate(user) && Role.isRecruiterDefault(user)) ||
        (!Role.isRecruiter(user) && !Role.isCandidate(user))
        ? '#'
        : ViewAsRecruiter === 'Yes' || ViewProfile === 'Yes'
        ? LinkBuilder.getProfileLink(CandidateID)
        : '#';
    }

    if ([9, 28].indexOf(type) !== -1) {
      return FirmUrlAlias ? LinkBuilder.getFirmLink(FirmUrlAlias) : '#';
    }

    if ([10, 21].indexOf(type) !== -1) {
      return AllowView === 'Yes'
        ? LinkBuilder.getInterviewLink(FirmUrlAlias, PublicName)
        : '#';
    }

    if ([11, 20].indexOf(type) !== -1) {
      return AllowView === 'Yes'
        ? LinkBuilder.getEmployeeLink(FirmUrlAlias, PublicName)
        : '#';
    }

    if ([26, 27].indexOf(type) !== -1) {
      return AllowView === 'Yes'
        ? LinkBuilder.getOverviewLink(FirmUrlAlias, PublicName)
        : '#';
    }

    if ([16, 18].indexOf(type) !== -1) {
      return Role.isCandidate(user)
        ? ProcessType === 'Job'
          ? LinkBuilder.getJobLink(ProcessID)
          : LinkBuilder.getProjectLink(ProcessID)
        : '#';
    }

    return null;
  }, [user]);

  const anonymousMessageTypes = [5, 19, 22, 24];

  const onAvatarClick = event => {
    if ([10, 11, 20, 21, 26, 27].indexOf(type) !== -1) {
      onClickReviewLink(event, {
        TypeName: FirmTypeName,
        ReviewType,
        AllowView,
      });
    } else if ([1, 2, 4, 5, 6, 7, 8/*, 19*/, 22, /*24,*/ 25].indexOf(type) !== -1) {
      if (Role.isRecruiterDefault(user) && !Role.isCandidate(user)) {
        UpgradeRecruiterAccount();
        event.preventDefault();
      } else if (!Role.isRecruiter(user) && !Role.isCandidate(user, true)) {
        event.preventDefault();
      }
    } else if ([16, 18].indexOf(type) !== -1) {
      if (!Role.isCandidate(user, true)) {
        event.preventDefault();
      }
    } else if (anonymousMessageTypes.indexOf(type) !== -1) {
        event.preventDefault();
    }
  };

  const onApply = () => {
    onClickApplyHandler();
  };

  const onConnect = () => {
    if (!isDisableActions && Role.isCandidate(user, true)) {
      const action = new NetworkActions(api);
      setDisableActions(true);
      action
        .connect({ id: CandidateID })
        .then(
          () =>
            onStatusChangeHandler &&
            onStatusChangeHandler(CandidateID, CONNECT_STATUS.CONNECTED)
        )
        .finally(() => setDisableActions(false));
    }
  };

  const onSendMessage = () => {
    onSendMessageHandler(post);
  };

  return (
    <div className={cls}>
      <Avatar
        size={size}
        type={iconType}
        link={anonymousMessageTypes.indexOf(type) === -1 ? link : ''}
        image={anonymousMessageTypes.indexOf(type) === -1 ? avatar : null}
        controlFunc={onAvatarClick}
      />
      {HeroStatus && anonymousMessageTypes.indexOf(type) === -1 && (
        <FeedPostStatus
          status={HeroStatus}
          actions={{
            onApply: onApply,
            onConnect: onConnect,
            onMessage: onSendMessage,
          }}
        />
      )}
    </div>
  );
};

FeedPostAvatar.propTypes = {
  isMobile: PropTypes.bool,
  user: PropTypes.object.isRequired,
  post: PropTypes.object.isRequired,
  actions: PropTypes.shape({
    onStatusChangeHandler: PropTypes.func,
    onClickReviewLink: PropTypes.func,
    onClickApplyHandler: PropTypes.func,
    onSendMessageHandler: PropTypes.func,
  }).isRequired,
};

export default FeedPostAvatar;
